import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingBidMarket } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { SideMenuDot } from '~/models/sideMenuDot'
import { routePaths } from '~/routes/routePaths'

/** 区分不明の札の量を決める札 1 件 (ブロックは leg のコマ 1 つ) の値。 各市場が自分のステータスから作る。 */
export type UnknownIssuerBidQuantityInput = {
  /** 入札後ポジションに数えるステータスか。 取消・失効は false。 */
  counted: boolean
  direction: Side
  /**
   * 札がポジションに数える量 MW。 各 builder が札ごとに 1 回だけ決めた値
   * (`coverBidMw`: 部分約定は約定量、 それ以外は入札した時点の量) をそのまま渡す。
   */
  mw: number
}

/**
 * 発注主体区分が空欄の札を入札後ポジションの「区分不明の札」に数える量 MW (AC-BSP-127 /
 * AC-BIN-129、 #10084)。 売りは負にするのでコマごとに足すと買 − 売のネットになる。
 * 数えない札は undefined。 入札後カバー量にも約定量にも入れない量なので、
 * 呼び出し側はこの量だけを別の項目に積む。
 */
export const unknownIssuerBidMw = ({
  counted,
  direction,
  mw,
}: UnknownIssuerBidQuantityInput): number | undefined => {
  if (!counted) return undefined
  return direction === Side.SELL ? -mw : mw
}

/** 警告に並べる受渡契約コード。 出現順に重複を除き、 空のコードは並べない (#10084)。 */
export const uniqueDeliveryContractCodes = (
  codes: Iterable<string | undefined>,
): string[] => {
  const unique = new Set<string>()
  for (const code of codes) {
    if (code !== undefined && code !== '') unique.add(code)
  }
  return [...unique]
}

const MARKET_MENU_PATH: Partial<Record<BiddingBidMarket, string>> = {
  [BiddingBidMarket.SPOT]: routePaths.biddingSpot.path,
  [BiddingBidMarket.INTRADAY]: routePaths.biddingIntraday.path,
}

/**
 * AC-SC-043: 発注主体区分が空欄の札がある市場の項目に黄のドットを作る。 親グループの
 * ドットは子の項目からまとまる (#10084)。
 *
 * @param reason 表示文言 (i18n 解決済み)
 */
export const unknownIssuerBidDots = (
  markets: readonly BiddingBidMarket[],
  reason: string,
): Array<readonly [string, SideMenuDot]> => {
  const paths = new Set<string>()
  for (const market of markets) {
    const path = MARKET_MENU_PATH[market]
    if (path !== undefined) paths.add(path)
  }
  return [...paths].map(
    (path) => [path, { severity: 'warning', reasons: [reason] }] as const,
  )
}
